import { Globe, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import { useState, type ChangeEvent, type FormEvent } from 'react'
import { api, errorText } from '../api.ts'
import { Button, buttonClass } from '../components/Button.tsx'
import { useCompany, useCompanyCache } from '../company.ts'
import type { Company } from '../types.ts'
import { CompanyMark, Field, Loading, Modal, Notice } from '../ui.tsx'

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']

const customerSince = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' })

const logoSrc = (company: Company) =>
  company.logo ? `/dashboard/api/companies/${company.id}/logo?v=${company.logo.sha256.slice(0, 12)}` : null

/** The company's name, logo and permitted email domains (PLAN.md 1.6). */
export function CompanySettings({ companyId }: { companyId: string }) {
  const detail = useCompany(companyId)
  const [editing, setEditing] = useState<'logo' | 'name' | 'domain' | null>(null)
  if (detail.isPending) return <Loading />
  if (detail.isError) return <Notice>{errorText(detail.error)}</Notice>
  const { company } = detail.data
  const close = () => setEditing(null)

  return (
    <section>
      <div className="company-head">
        <button type="button" className="logo-edit" onClick={() => setEditing('logo')} aria-label="Change the company logo">
          <CompanyMark name={company.name} logo={logoSrc(company)} size={72} />
          <span className="logo-edit-overlay" aria-hidden="true"><Pencil /></span>
        </button>
        <div className="company-head-text">
          <h1>{company.name}</h1>
          <p className="muted">Customer since {customerSince.format(new Date(company.createdAt))}</p>
        </div>
        <button type="button" className="icon-button icon-button-plain company-head-edit" onClick={() => setEditing('name')} aria-label="Rename the company" title="Rename">
          <Pencil aria-hidden="true" />
        </button>
      </div>
      <Domains company={company} onAdd={() => setEditing('domain')} />

      <Modal title="Company logo" open={editing === 'logo'} onClose={close}>
        <LogoForm company={company} />
      </Modal>
      <Modal title="Rename company" open={editing === 'name'} onClose={close}>
        <NameForm company={company} onDone={close} />
      </Modal>
      <Modal title="Add email domain" open={editing === 'domain'} onClose={close}>
        <DomainForm company={company} onDone={close} />
      </Modal>
    </section>
  )
}

/** Saves a change to the company and puts ICE's answer in the cache. */
function useSaveCompany(company: Company) {
  const cache = useCompanyCache(company.id)
  return async (patch: { name?: string; domains?: string[] }) => {
    cache.putCompany(await api.patch<Company>(`/companies/${company.id}`, { version: company.version, ...patch }))
  }
}

function Domains({ company, onAdd }: { company: Company; onAdd: () => void }) {
  const save = useSaveCompany(company)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const remove = async (domain: string) => {
    setBusy(true)
    setError(null)
    try {
      await save({ domains: company.domains.filter((d) => d !== domain) })
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h2>Email domains</h2>
          <p className="muted small">The email domains your users' Microsoft accounts use.</p>
        </div>
        <Button variant="secondary" icon={Plus} onClick={onAdd}>Add domain</Button>
      </div>
      <Notice>{error}</Notice>
      {company.domains.length === 0 ? (
        <p className="muted">No domains yet.</p>
      ) : (
        <ul className="list-rows">
          {company.domains.map((d) => (
            <li key={d}>
              <Globe className="list-icon" aria-hidden="true" />
              <span className="list-text">{d}</span>
              <button type="button" className="icon-button icon-button-danger" disabled={busy} onClick={() => remove(d)} aria-label={`Remove ${d}`}>
                <Trash2 aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function DomainForm({ company, onDone }: { company: Company; onDone: () => void }) {
  const save = useSaveCompany(company)
  const [domain, setDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const value = domain.trim().toLowerCase().replace(/^@/, '')
    if (company.domains.includes(value)) return setError(`${value} is already on the list`)
    setBusy(true)
    setError(null)
    try {
      await save({ domains: [...company.domains, value] })
      onDone()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice>{error}</Notice>
      <Field label="Domain" hint="As in example.com, without the @.">
        <input required autoFocus value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" />
      </Field>
      <div className="form-actions">
        <Button type="submit" disabled={busy}>Add</Button>
      </div>
    </form>
  )
}

function NameForm({ company, onDone }: { company: Company; onDone: () => void }) {
  const save = useSaveCompany(company)
  const [name, setName] = useState(company.name)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await save({ name })
      onDone()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice>{error}</Notice>
      <Field label="Name">
        <input required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="form-actions">
        <Button type="submit" disabled={busy}>Save</Button>
      </div>
    </form>
  )
}

function LogoForm({ company }: { company: Company }) {
  const cache = useCompanyCache(company.id)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (work: () => Promise<Company>) => {
    setBusy(true)
    setError(null)
    try {
      cache.putCompany(await work())
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const upload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!LOGO_TYPES.includes(file.type)) return setError('Use a PNG, JPEG, WebP or SVG logo')
    if (file.size > 512 * 1024) return setError('Keep the logo under 512 KB')
    void run(() => api.put<Company>(`/companies/${company.id}/logo`, file, file.type))
  }

  return (
    <div className="form">
      <Notice>{error}</Notice>
      <div className="logo-row">
        <CompanyMark name={company.name} logo={logoSrc(company)} size={72} />
        <div className="logo-actions">
          <label className={buttonClass({ variant: 'secondary' })}>
            <Upload aria-hidden="true" />
            {company.logo ? 'Replace' : 'Upload'}
            <input type="file" accept={LOGO_TYPES.join(',')} hidden disabled={busy} onChange={upload} data-autosave />
          </label>
          {company.logo && (
            <Button variant="destructive-tertiary" icon={Trash2} disabled={busy} onClick={() => run(() => api.delete<Company>(`/companies/${company.id}/logo`))}>
              Remove
            </Button>
          )}
        </div>
      </div>
      <p className="muted small">PNG, JPEG, WebP or SVG, up to 512 KB. Shown on the dashboard and when choosing a company.</p>
    </div>
  )
}
